import type { IPage, IVersion } from "@/types";
import type { StaticEntityType } from "@config";
import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import type { ShareDialogState } from "./types";

const initialState: ShareDialogState = {
  viewId: "",
  versions: [],
  layout: "list",
  selectedVersion: null,
  primaryVersion: null,
  pageId: null,
  page: null,
  versionHandleType: null,
  dialogOpen: false,
  ogUploadStatus: {
    success: false,
    type: "",
    isLoading: false,
  },
  pdfExportOptions: {
    layout: "multi-slide",
    slides: null,
    highquality: false,
    minPageRatio: false,
    removeQRCode: false,
  },
  versionsLoading: { isLoading: false, isFetching: false },
  searchOptions: {
    pageId: null,
    search: "",
    size: 25,
    sortOrder: "descending",
    page: 0,
    userIds: null,
  },
  bulkSendView: "template",
  multiSelectedVersions: {},
  totalVersionsCount: 0,
  staticMode: false,
  slides: [],
  abortThumbnailProcess: false,
  autogeneratePreviewVersionId: null,
  overrideContentPointer: null,
};

export const shareDialogSlice = createSlice({
  name: "share-dialog",
  initialState: initialState,
  reducers: {
    resetState: () => initialState,
    setPageId: (state: ShareDialogState, action: PayloadAction<string>) => {
      state.pageId = action.payload;
      state.searchOptions.pageId = action.payload;
    },
    setPage: (state: ShareDialogState, action: PayloadAction<IPage | null>) => {
      state.page = action.payload;
    },
    updatePageOgImage: (
      state: ShareDialogState,
      action: PayloadAction<{ ogImage: string; versionsId: string }>,
    ) => {
      const { ogImage, versionsId } = action.payload;
      if (!state.page?.settings || versionsId !== state.primaryVersion?._id)
        return;
      state.page.settings.og.image = ogImage;
    },
    setView: (state: ShareDialogState, action: PayloadAction<string>) => {
      state.viewId = action.payload;
      state.pdfExportOptions = initialState.pdfExportOptions;
      state.selectedVersion = null;
      state.versionHandleType = null;
      state.bulkSendView = initialState.bulkSendView;
      if (action.payload === "N9rn01DLwPMfRRD3GjMW") {
        state.searchOptions.search = "";
        state.searchOptions.sortOrder = "descending";
        state.searchOptions.page = 0;
      }
      state.multiSelectedVersions = {};
      state.totalVersionsCount = 0;
      state.overrideContentPointer = null;
    },
    setVersions: (
      state: ShareDialogState,
      action: PayloadAction<IVersion[]>,
    ) => {
      state.versions = action.payload;
    },
    updateVersion: (
      state: ShareDialogState,
      action: PayloadAction<IVersion>,
    ) => {
      const version = action.payload;
      if (version._id === state.primaryVersion?._id) {
        state.primaryVersion = version;
      }
      const index = state.versions.findIndex((v) => v._id === version._id);
      if (index > -1) state.versions[index] = version;
    },
    deleteVersion: (state: ShareDialogState, action: PayloadAction<string>) => {
      const id = action.payload;
      const index = state.versions.findIndex((v) => v._id === id);
      if (index > -1) state.versions.splice(index, 1);
    },
    createVersion: (
      state: ShareDialogState,
      action: PayloadAction<IVersion>,
    ) => {
      const version = action.payload;
      state.versions.unshift(version);
    },
    setLayout: (state: ShareDialogState, action: PayloadAction<string>) => {
      state.layout = action.payload;
    },
    setVersion: (
      state: ShareDialogState,
      action: PayloadAction<IVersion | null>,
    ) => {
      state.selectedVersion = action.payload;
    },
    setPrimaryVersion: (
      state: ShareDialogState,
      action: PayloadAction<IVersion | null>,
    ) => {
      state.primaryVersion = action.payload;
    },
    setPdfExportOptions: (
      state: ShareDialogState,
      action: PayloadAction<Partial<typeof state.pdfExportOptions>>,
    ) => {
      state.pdfExportOptions = { ...state.pdfExportOptions, ...action.payload };
    },
    setDialogOpen: (
      state: ShareDialogState,
      action: PayloadAction<boolean>,
    ) => {
      const status = action.payload;
      state.dialogOpen = status;
      if (!status) Object.assign(state, initialState);
    },
    setVersionHandleType: (
      state: ShareDialogState,
      action: PayloadAction<"edit" | "duplicate" | "sync" | "update" | null>,
    ) => {
      state.versionHandleType = action.payload;
    },
    updateOgUploadStatus: (
      state: ShareDialogState,
      action: PayloadAction<Record<string, string | boolean>>,
    ) => {
      state.ogUploadStatus = { ...state.ogUploadStatus, ...action.payload };
    },
    setSearchOption: (
      state: ShareDialogState,
      action: PayloadAction<{
        key: "search" | "sortOrder" | "page" | "userIds";
        value: string | number | string[] | null;
      }>,
    ) => {
      const { key, value } = action.payload;
      state.searchOptions = { ...state.searchOptions, [key]: value };
    },
    setVersionsLoading: (
      state: ShareDialogState,
      action: PayloadAction<Record<string, boolean>>,
    ) => {
      const key = Object.keys(action.payload)[0] as "isLoading" | "isFetching";
      state.versionsLoading[key] = action.payload[key];
    },
    setBulkSendView: (
      state: ShareDialogState,
      action: PayloadAction<"template" | "upload" | "success">,
    ) => {
      state.bulkSendView = action.payload;
    },
    setVersionIntoMultiSelect: (
      state: ShareDialogState,
      action: PayloadAction<
        | { id: string; type?: null; userId?: string }
        | {
            id: null;
            type: "add-all" | "remove-all" | "all-live";
            userId?: string;
          }
      >,
    ) => {
      const { id, type, userId } = action.payload;
      switch (type) {
        case "all-live":
          state.multiSelectedVersions = { live: "live" };
          break;
        case "add-all":
          state.multiSelectedVersions = state.versions.reduce(
            (acc, version) => {
              if (version?.seal || !version.url) return acc;
              if (userId && version.userId !== userId) return acc;
              acc[version._id] = version.url ? "live" : "draft";
              return acc;
            },
            {} as Record<string, string>,
          );
          break;
        case "remove-all":
          state.multiSelectedVersions = {};
          break;
        default:
          if (id) {
            const version = state.versions.find((v) => v._id === id);
            const selected = state.multiSelectedVersions[id];
            const allLive = state.multiSelectedVersions.live === "live";

            if (selected) delete state.multiSelectedVersions[id];
            else {
              if (allLive) state.multiSelectedVersions = {};
              if (version?.seal) return;
              state.multiSelectedVersions[id] = version?.url ? "live" : "draft";
            }
          }
          break;
      }
    },
    setStaticMode: (
      state: ShareDialogState,
      action: PayloadAction<boolean>,
    ) => {
      state.staticMode = action.payload;
    },
    setTotalVersionsCount: (
      state: ShareDialogState,
      action: PayloadAction<number>,
    ) => {
      state.totalVersionsCount = action.payload;
    },
    setSlides: (
      state: ShareDialogState,
      action: PayloadAction<StaticEntityType[]>,
    ) => {
      state.slides = action.payload;
    },
    setAbortThumbnailProcess: (state: ShareDialogState) => {
      state.abortThumbnailProcess = true;
    },
    resetAbortThumbnailProcess: (state: ShareDialogState) => {
      state.abortThumbnailProcess = false;
    },
    handleAutoThumbnailVersionId: (
      state: ShareDialogState,
      action: PayloadAction<string | null>,
    ) => {
      state.autogeneratePreviewVersionId = action.payload;
    },
    setOverrideContentPointer: (
      state: ShareDialogState,
      action: PayloadAction<string | null>,
    ) => {
      state.overrideContentPointer = action.payload;
    },
    resetMultiselect: (state: ShareDialogState) => {
      state.multiSelectedVersions = {};
      state.totalVersionsCount = 0;
    },
  },
});

export const {
  resetState,
  setView,
  setLayout,
  setVersion,
  setPdfExportOptions,
  setPageId,
  setPage,
  setPrimaryVersion,
  setDialogOpen,
  updateOgUploadStatus,
  setVersionHandleType,
  setVersions,
  updateVersion,
  createVersion,
  deleteVersion,
  setVersionsLoading,
  setSearchOption,
  updatePageOgImage,
  setBulkSendView,
  setVersionIntoMultiSelect,
  setStaticMode,
  setTotalVersionsCount,
  resetMultiselect,
  setSlides,
  setAbortThumbnailProcess,
  resetAbortThumbnailProcess,
  handleAutoThumbnailVersionId,
  setOverrideContentPointer,
} = shareDialogSlice.actions;
